/**
 * Launcher tile derivation — one tile per workspace surface.
 *
 * The launcher (`/launch`, ADR-0049) shows the same navigation universe as
 * the sidebar, so tiles are DERIVED from `NAV_GROUPS` via `visibleNavGroups`
 * rather than declared twice: module entitlements, permission gating, and
 * the live-route check all come along for free. Shape of the mapping:
 *
 *   - The unlabelled "Workspace" group (Home / Ask Flint / Inbox) becomes
 *     one tile PER ITEM — these are destinations, not containers.
 *   - Every labelled content group (Sales, Procurement, Cash, …) becomes ONE
 *     tile: the group's icon, its first live route, and a sub-line listing
 *     what's inside.
 *   - The unlabelled "Settings" group contributes only the Settings tile
 *     (Help/Support are external links, not modules).
 *
 * Tints are cosmetic grouping only — status meaning stays with StatusPill
 * surfaces; here color just separates business areas (brand-spec §2.3 is
 * satisfied because tiles never encode state by color alone).
 */
import type { LucideIcon } from "lucide-react";
import { visibleNavGroups, type NavItem } from "../lib/nav-config.ts";
import { AI_TEAMMATE_NAME, AI_TEAMMATE_ROLE } from "../components/teammate-avatar.tsx";

export type LauncherTint = "sky" | "ok" | "warn" | "bad" | "slate" | "ai";

export interface LauncherTile {
  /** Stable identity — group ariaLabel, or the item route for Workspace items. */
  key: string;
  label: string;
  /** One-line contents summary shown under the label. */
  sub: string;
  icon: LucideIcon;
  /** First live route inside the surface. */
  to: string;
  /** Every live route the surface owns — active-tile matching uses ALL of
   *  them, so `/merchandising/styles` still reads "Merchandising" in the switcher. */
  routes: string[];
  tint: LauncherTint;
  /**
   * Render as the Flint teammate persona (FlintAvatar face instead of an
   * icon chip). Set only on the assistant tile — Flint is presented as a
   * colleague you message, not a feature you open.
   */
  persona?: boolean;
}

/** Business-area tint per group (`ariaLabel` key). Unlisted → slate. */
const GROUP_TINTS: Record<string, LauncherTint> = {
  Sales: "sky",
  Store: "sky",
  "Point of Sale": "ok",
  Procurement: "warn",
  Inventory: "slate",
  Payroll: "sky",
  Cash: "ok",
  Merchandising: "sky",
  Production: "slate",
  Documents: "slate",
  Compliance: "bad",
  Reports: "sky",
  Records: "slate",
};

/** Sub-lines for the Workspace group's single-destination tiles. */
const WORKSPACE_ITEM_SUBS: Record<string, string> = {
  "/": "Daily briefing",
  "/ask": AI_TEAMMATE_ROLE,
  "/approvals": "Approvals",
};

/** Items that are real, clickable SPA destinations. */
function liveItems(items: NavItem[]): NavItem[] {
  return items.filter((item) => !item.disabled && !item.external && item.to !== "#");
}

/**
 * The launcher grid for a member, in sidebar order. Same inputs as the
 * sidebar: the company's entitled modules + the member's permissions.
 */
export function launcherTiles(
  modules: readonly string[],
  permissions: readonly string[] = [],
): LauncherTile[] {
  const tiles: LauncherTile[] = [];

  for (const group of visibleNavGroups(modules, permissions)) {
    if (group.ariaLabel === "Workspace") {
      for (const item of liveItems(group.items)) {
        const persona = item.to === "/ask";
        tiles.push({
          key: item.to,
          // The assistant reads as a teammate (the AI employee's name),
          // not an action label.
          label: persona ? AI_TEAMMATE_NAME : item.label,
          sub: WORKSPACE_ITEM_SUBS[item.to] ?? "",
          icon: item.icon,
          to: item.to,
          routes: [item.to],
          tint: item.ai ? "ai" : item.to === "/approvals" ? "warn" : "slate",
          ...(persona ? { persona: true } : {}),
        });
      }
      continue;
    }

    if (group.ariaLabel === "Settings") {
      const settings = liveItems(group.items).find((item) => item.to === "/settings");
      if (settings) {
        tiles.push({
          key: "Settings",
          label: settings.label,
          sub: "Company · Team · Billing",
          icon: settings.icon,
          to: settings.to,
          routes: [settings.to],
          tint: "slate",
        });
      }
      continue;
    }

    const live = liveItems(group.items);
    if (live.length === 0) continue;
    tiles.push({
      key: group.ariaLabel,
      label: group.label ?? group.ariaLabel,
      sub: live
        .slice(0, 3)
        .map((item) => item.label)
        .join(" · "),
      icon: live[0]!.icon,
      to: live[0]!.to,
      routes: live.map((item) => item.to),
      tint: GROUP_TINTS[group.ariaLabel] ?? "slate",
    });
  }

  return tiles;
}

/**
 * Case-insensitive tile filter for the omnibox's search mode. Matches the
 * tile name and its contents line, so "proformas" finds the Sales tile.
 */
export function filterTiles(tiles: LauncherTile[], query: string): LauncherTile[] {
  const q = query.trim().toLowerCase();
  if (!q) return tiles;
  return tiles.filter(
    (tile) => tile.label.toLowerCase().includes(q) || tile.sub.toLowerCase().includes(q),
  );
}

export interface LauncherQuickAction {
  label: string;
  to: string;
  /** Permission required to offer the action (display-only; server is the floor). */
  permission?: string;
  /** Render with the AI (sky) treatment. */
  ai?: boolean;
}

/**
 * One-click creates under the omnibox — Priya's top tasks, straight from
 * persona-journeys (invoice in the 4-field flow, receipt, bill, reconcile).
 */
const QUICK_ACTIONS: LauncherQuickAction[] = [
  { label: "New invoice", to: "/sales/invoices/new", permission: "invoices.create" },
  { label: "Record receipt", to: "/sales/collections", permission: "receipts.record" },
  { label: "Add bill", to: "/procurement/bills/new", permission: "bills.create" },
  { label: "Reconcile bank", to: "/cash/reconcile", permission: "reconcile.view" },
  { label: "Today’s briefing", to: "/", ai: true },
];

/** Quick actions the member may take (same `*`-wildcard rule as the nav). */
export function visibleQuickActions(permissions: readonly string[]): LauncherQuickAction[] {
  const isOwner = permissions.includes("*");
  return QUICK_ACTIONS.filter(
    (action) => !action.permission || isOwner || permissions.includes(action.permission),
  );
}
